import { Modal,Pagination,Table,Tag } from "antd";
import {
  deleteJobDriveSlotApi,
  getJobDriveSlotsApi,
  getJobListApi
} from "api/services/jobs";
import ScheduleSlotModal from "components/Administration/Jobs/JobDrive/ScheduleSlotModal";
import Breadcrumbs from "components/Breadcrumbs";
import { Button } from "components/Button";
import { SkeltonLoader } from "components/skeltonLoader";
import { format,parseISO } from "date-fns";
import { useCallback,useEffect,useMemo,useState } from "react";
import { useSelector } from "react-redux";
import { Link,Navigate } from "react-router-dom";
import Select from "react-select";
import { Bounce,toast } from "react-toastify";
import { customStyleSelect } from "utils/common";
import {
  itemRender,
  pageSizeOptions,
} from "utils/table";

const JobDriveSlots = () => {
  const { user, userLoading } = useSelector((state) => state.user);
  
  const [loading, setLoading] = useState(false);
  const [slots, setSlots] = useState([]);
  const [slotModalOpen, setSlotModalOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [slotToDelete, setSlotToDelete] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [pageSettings, setPageSettings] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
  });
  const [view, setView] = useState("list"); // "list" or "grid"

  const fetchJobs = useCallback(async () => {
    setJobsLoading(true);
    try {
      const response = await getJobListApi({ job_drive_enabled: true });
      setJobs(response.data?.results || []);
    } catch (error) {
      console.error("Error fetching jobs:", error);
      toast.error("Failed to load jobs", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setJobsLoading(false);
    }
  }, []);

  const fetchSlots = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedJob?.value) {
        params.job_id = selectedJob.value;
      }
      const response = await getJobDriveSlotsApi(params);
      // Handle different response structures: could be array, object with results, or object with slots
      const slotsData = response.data;
      let slotsArray = [];
      if (Array.isArray(slotsData)) {
        slotsArray = slotsData;
      } else if (slotsData?.results && Array.isArray(slotsData.results)) {
        slotsArray = slotsData.results;
      } else if (slotsData?.slots && Array.isArray(slotsData.slots)) {
        slotsArray = slotsData.slots;
      }
      setSlots(slotsArray);
    } catch (error) {
      console.error("Error fetching slots:", error);
      setSlots([]); // Ensure slots is always an array even on error
      toast.error("Failed to load slots", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
  }, [selectedJob]);

  // Check if user has job_drive_slot permission
  const hasJobDrivePermission = useMemo(() => {
    // Wait until user is fully loaded
    if (userLoading) return null; // null means "still checking"
    // Check if user object is empty (initial state is {})
    if (!user || Object.keys(user).length === 0 || !user.id) return null; // Still waiting for user data
    // If user has no role or modules, they don't have permission
    if (!user?.role?.permissions && !user?.modules) return false;
    // Check for job_drive_slot permission
    return user.role?.permissions?.some((p) => p.code === "job_drive_slot") ||
           user?.modules?.some?.((m) => m.code === "job_drive_slot");
  }, [user, userLoading]);

  useEffect(() => {
    // Only fetch data if user has permission
    if (hasJobDrivePermission === true) {
      fetchJobs();
    }
  }, [fetchJobs, hasJobDrivePermission]);

  useEffect(() => {
    // Only fetch data if user has permission
    if (hasJobDrivePermission === true) {
      fetchSlots();
    }
  }, [fetchSlots, hasJobDrivePermission]);

  // Update total when slots change
  useEffect(() => {
    setPageSettings(prev => ({
      ...prev,
      total: Array.isArray(slots) ? slots.length : 0,
    }));
  }, [slots]);

  // Wait for user to load before checking permissions
  if (userLoading || hasJobDrivePermission === null) {
    return <div>Loading...</div>;
  }

  // Redirect if no permission (after hooks)
  if (hasJobDrivePermission === false) {
    return <Navigate to="/manage-jobs" replace />;
  }

  const handleCreateSlot = () => {
    setSelectedSlot(null);
    setSlotModalOpen(true);
  };

  const handleEditSlot = (slot) => {
    setSelectedSlot(slot);
    setSlotModalOpen(true);
  };

  const handleDeleteSlot = (slot) => {
    setSlotToDelete(slot);
    setDeleteModalOpen(true);
  };

  const confirmDeleteSlot = async () => {
    if (!slotToDelete) return;
    try {
      await deleteJobDriveSlotApi(slotToDelete.id);
      toast.success("Time slot deleted successfully!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
      fetchSlots();
      setDeleteModalOpen(false);
      setSlotToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete slot", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    }
  };

  // Prepare job options
  const jobOptions = jobs?.map(job => ({
    label: job.title,
    value: job.id
  })) || [];

  const slotColumns = [
    {
      title: "#",
      dataIndex: "id",
      sorter: true,
    },
    {
      title: "Job",
      render: (_, record) => {
        // Handle job as object or ID
        if (record.job && typeof record.job === 'object') {
          return record.job.title || "-";
        }
        const job = jobs.find(j => j.id === record.job);
        return job ? job.title : record.job || "-";
      },
      sorter: true,
    },
    {
      title: "Title",
      dataIndex: "title",
      sorter: true,
    },
    {
      title: "Date & Time",
      render: (_, record) => {
        if (record.start_at && record.end_at) {
          const startDate = parseISO(record.start_at);
          const endDate = parseISO(record.end_at);
          return (
            <div>
              <div>{format(startDate, "dd-MM-yyyy")}</div>
              <div style={{ fontSize: "12px", color: "#666" }}>
                {format(startDate, "HH:mm")} - {format(endDate, "HH:mm")}
              </div>
            </div>
          );
        }
        return "-";
      },
      sorter: true,
    },
    {
      title: "Location",
      dataIndex: "location",
      render: (text) => text || "-",
      sorter: true,
    },
    {
      title: "Capacity",
      render: (_, record) => `${record.invited_count || 0} / ${record.capacity}`,
      sorter: true,
    },
    {
      title: "Checked In",
      render: (_, record) => record.checked_in_count || 0,
      sorter: true,
    },
    {
      title: "Status",
      render: (_, record) => {
        const isFull = (record.invited_count || 0) >= record.capacity;
        const statusColor = record.status === "active" ? (isFull ? "orange" : "green") : "default";
        return (
          <div className="dropdown action-label text-center">
            <Tag color={statusColor}>
              {record.status === "active" ? (isFull ? "Full" : "Available") : record.status}
            </Tag>
          </div>
        );
      },
      sorter: true,
    },
    {
      title: "Action",
      render: (_, record) => (
        <div className="dropdown dropdown-action text-end">
          <Link
            to="#"
            className="action-icon dropdown-toggle"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <i className="material-icons">more_vert</i>
          </Link>
          <div className="dropdown-menu dropdown-menu-right">
            <Link
              onClick={() => handleEditSlot(record)}
              className="dropdown-item"
            >
              <i className="fa fa-pencil m-r-5" /> Edit
            </Link>
            <Link
              to="#"
              className="dropdown-item"
              onClick={(e) => {
                e.preventDefault();
                handleDeleteSlot(record);
              }}
            >
              <i className="fa fa-trash m-r-5" /> Delete
            </Link>
          </div>
        </div>
      ),
    },
  ];

  const handleChange = (pagination, filters, sorter) => {
    setPageSettings({
      ...pageSettings,
      page: pagination.current,
      pageSize: pagination.pageSize,
    });
    // You can add sorting/filtering logic here if needed
    fetchSlots();
  };

  const handleGridChange = (page, pageSize) => {
    setPageSettings({
      ...pageSettings,
      page,
      pageSize,
    });
    fetchSlots();
  };

  const GridView = () => {
    if (loading) {
      return (
        <SkeltonLoader
          number={pageSettings?.pageSize}
          active={true}
          className="col-md-4 col-sm-6 col-12 col-lg-4 col-xl-3 p-2"
        />
      );
    } else {
      return (
        <>
          {slots.map((slot) => (
            <div
              className="col-12 col-sm-6 col-md-4 col-lg-3"
              key={slot.id}
              style={{
                marginBottom: "16px",
                display: "flex"
              }}
            >
              <div className="card shadow-sm" style={{ 
                borderRadius: "8px", 
                border: "none",
                background: "linear-gradient(135deg, #ffffff 0%, #fef2f2 100%)",
                margin: 0,
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                position: "relative",
                overflow: "visible",
                transition: "all 0.2s ease",
                height: "100%",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                cursor: "pointer"
              }}
              onClick={() => handleEditSlot(slot)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
              }}
              >
                {/* Action Buttons - Top Right */}
                <div 
                  style={{
                    position: "absolute",
                    top: "8px",
                    right: "8px",
                    display: "flex",
                    gap: "4px",
                    zIndex: 10
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => handleEditSlot(slot)}
                    style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "6px",
                      border: "none",
                      background: "rgba(59, 130, 246, 0.1)",
                      color: "#3b82f6",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      fontSize: "14px"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#3b82f6";
                      e.currentTarget.style.color = "#fff";
                      e.currentTarget.style.transform = "scale(1.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "rgba(59, 130, 246, 0.1)";
                      e.currentTarget.style.color = "#3b82f6";
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                    title="Edit Slot"
                  >
                    <i className="la la-pencil" style={{ fontSize: "12px" }}></i>
                  </button>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleDeleteSlot(slot);
                    }}
                    style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "6px",
                      border: "none",
                      background: "rgba(239, 68, 68, 0.1)",
                      color: "#ef4444",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      fontSize: "14px"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#ef4444";
                      e.currentTarget.style.color = "#fff";
                      e.currentTarget.style.transform = "scale(1.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "rgba(239, 68, 68, 0.1)";
                      e.currentTarget.style.color = "#ef4444";
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                    title="Delete Slot"
                  >
                    <i className="la la-trash" style={{ fontSize: "12px" }}></i>
                  </button>
                </div>

                <div className="card-body" style={{ 
                  padding: "16px",
                  flex: 1,
                  display: "flex",
                  flexDirection: "column"
                }}>
                  {/* Slot Title */}
                  <div style={{
                    marginBottom: "12px"
                  }}>
                    <div
                      style={{
                        color: "#1f2937",
                        fontSize: "15px",
                        fontWeight: "600",
                        display: "block",
                        marginBottom: "8px",
                        lineHeight: "1.4"
                      }}
                    >
                      {slot.title}
                    </div>
                    {slot.location && (
                      <div style={{
                        fontSize: "12px",
                        color: "#6b7280",
                        lineHeight: "1.5",
                        marginBottom: "10px"
                      }}>
                        <i className="la la-map-marker" style={{ fontSize: "12px", marginRight: "4px" }}></i>
                        {slot.location}
                      </div>
                    )}
                  </div>

                  {/* Slot Details */}
                  <div style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px"
                  }}>
                    {slot.start_at && slot.end_at && (
                      <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px"
                      }}>
                        <i className="la la-calendar" style={{ fontSize: "12px", color: "#9ca3af", width: "16px" }}></i>
                        <span style={{ fontSize: "12px", color: "#374151", fontWeight: "500" }}>
                          {format(parseISO(slot.start_at), "dd-MM-yyyy")}
                        </span>
                      </div>
                    )}
                    
                    {slot.start_at && slot.end_at && (
                      <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px"
                      }}>
                        <i className="la la-clock" style={{ fontSize: "12px", color: "#9ca3af", width: "16px" }}></i>
                        <span style={{ fontSize: "12px", color: "#374151", fontWeight: "500" }}>
                          {format(parseISO(slot.start_at), "HH:mm")} - {format(parseISO(slot.end_at), "HH:mm")}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Badges */}
                  <div style={{
                    marginTop: "12px",
                    paddingTop: "12px",
                    borderTop: "1px solid #f0f0f0",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "6px",
                    alignItems: "center",
                    justifyContent: "space-between"
                  }}>
                    <div style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "6px",
                      alignItems: "center"
                    }}>
                      <span 
                        className="badge badge-soft-primary"
                        style={{
                          fontSize: "10px",
                          padding: "4px 10px",
                          borderRadius: "6px",
                          fontWeight: "600"
                        }}
                      >
                        {slot.invited_count || 0} / {slot.capacity} Capacity
                      </span>
                      {slot.checked_in_count > 0 && (
                        <span 
                          className="badge badge-soft-success"
                          style={{
                            fontSize: "10px",
                            padding: "4px 10px",
                            borderRadius: "6px",
                            fontWeight: "600"
                          }}
                        >
                          {slot.checked_in_count} Checked In
                        </span>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: "6px", alignItems: "center", flexWrap: "wrap" }}>
                      {slot.status && (
                        <Tag color={slot.status === "active" ? "green" : "default"}>
                          {slot.status === "active" ? "Available" : slot.status}
                        </Tag>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </>
      );
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <Breadcrumbs
          maintitle="Job Drive Slots"
          title="Job Drive Slots"
          subtitle="Job Drive"
          modal="#add_slot"
          name="Create Slot"
          view={view}
          setJobModal={handleCreateSlot}
          setView={setView}
          isGrid={false}
        />

        <div className="row mb-4">
          <div className="col-lg-4 col-md-12">
            <div className="input-block mb-3">
              <label className="col-form-label">Filter by Job</label>
              <Select
                placeholder="Select a job"
                value={selectedJob}
                onChange={(option) => {
                  setSelectedJob(option);
                  setPageSettings(prev => ({ ...prev, page: 1 }));
                }}
                options={jobOptions}
                styles={customStyleSelect}
                isClearable
                isLoading={jobsLoading}
              />
            </div>
          </div>
        </div>

        {view === "grid" ? (
          <div className="row">    
            <GridView />
            <div className="my-2 d-flex justify-content-end ">
              <div className="col-sm-12 col-md-6 ">
                <Pagination
                  pageSize={pageSettings?.pageSize}
                  pageSizeOptions={pageSizeOptions}
                  defaultCurrent={1}
                  current={pageSettings?.page}
                  total={pageSettings?.total}
                  onChange={handleGridChange}
                  showLessItems
                  showSizeChanger={false}
                  showQuickJumper={false}
                  hideOnSinglePage
                  itemRender={(page, type, originalElement) => {
                    if (type === "next") {
                      return (
                        <Button
                          loading={loading}
                          className="btn btn-primary "
                          label="Load More"
                        />
                      );
                    }
                    return null;
                  }}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="row">
            <div className="col-12">
              <div className="card shadow-sm" style={{ borderRadius: "12px", border: "1px solid #e0e0e0", overflow: "hidden" }}>
                <style>{`
                  .modern-table .ant-table-tbody > tr {
                    background-color: #ffffff !important;
                  }
                  .modern-table .ant-table-tbody > tr:hover > td {
                    background-color: #f5f5f5 !important;
                  }
                  .modern-table .ant-table-thead > tr > th {
                    background-color: #f8f9fa !important;
                    font-weight: 600;
                    color: #333;
                    border-bottom: 2px solid #e0e0e0;
                    padding: 10px 12px;
                    font-size: 13px;
                  }
                  .modern-table .ant-table-tbody > tr > td {
                    border-bottom: 1px solid #f0f0f0;
                    padding: 10px 12px;
                    font-size: 13px;
                  }
                  .modern-table .ant-table-wrapper .ant-table-pagination.ant-pagination {
                    border-top: 1px solid #f0f0f0;
                    background-color: #ffffff;
                    border-bottom-left-radius: 12px;
                    border-bottom-right-radius: 12px;
                  }
                `}</style>
                <div className="table-responsive" style={{ overflowX: "auto" }}>
                  <Table
                    className="mb-0 modern-table"
                    style={{ 
                      borderRadius: "12px",
                      minWidth: "100%"
                    }}
                    scroll={{ x: "max-content" }}
                    columns={slotColumns}
                    dataSource={Array.isArray(slots) ? slots : []}
                    rowKey={(record) => record.id}
                    loading={loading}
                    pagination={{
                      total: pageSettings?.total,
                      pageSize: pageSettings?.pageSize,
                      showSizeChanger: true,
                      pageSizeOptions: pageSizeOptions,
                      itemRender: itemRender,
                      position: "right",
                      current: pageSettings?.page,
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} of ${total} items`,
                      style: { padding: "16px" }
                    }}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modals */}
        <ScheduleSlotModal
          open={slotModalOpen}
          onCancel={() => {
            setSlotModalOpen(false);
            setSelectedSlot(null);
          }}
          jobId={selectedJob?.value || null}
          slot={selectedSlot}
          onSuccess={() => {
            fetchSlots();
          }}
          loading={loading}
        />

        <Modal
          title="Delete Time Slot"
          open={deleteModalOpen}
          onOk={confirmDeleteSlot}
          onCancel={() => {
            setDeleteModalOpen(false);
            setSlotToDelete(null);
          }}
          okText="Delete"
          okButtonProps={{ danger: true }}
        >
          <p>Are you sure you want to delete this time slot?</p>
          {slotToDelete && (
            <p>
              <strong>Title:</strong> {slotToDelete.title || "N/A"}<br />
              {slotToDelete.start_at && slotToDelete.end_at && (
                <>
                  <strong>Date & Time:</strong> {format(parseISO(slotToDelete.start_at), "dd-MM-yyyy HH:mm")} - {format(parseISO(slotToDelete.end_at), "HH:mm")}
                </>
              )}
            </p>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default JobDriveSlots;

